import { Button, Card, CardActions, CardContent, CardHeader, List, ListItem, ListItemText, Menu, MenuItem } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as coreApi from "api/Core";
import * as kbApi from "api/Knowledge";
import { HeaderStylesClassKey } from "assets/jss/layouts/headerLinksStyle";
import IconButtonCog from "components/Buttons/IconButtonCog";
import IconButtonFilter from "components/Buttons/IconButtonFilter";
import { SpinnerIndicator } from "components/CallServer";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import IconNotification from "components/Icons/IconNotification";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import AppResource from "components/Translations/AppResources";
import { AccessKeys } from "Constants";
import HtmlUtils from "lib/HtmlUtils";
import { NotificationMainGroupType } from "models/ApiEnums";
import { NotificationDto, NotificationSetupItemDto } from "models/ApiModels";
import * as React from "react";
import { useState } from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Action, bindActionCreators } from 'redux';
import { ThunkDispatch } from "redux-thunk";
import * as AppActions from 'store/app/actions';
import { ApplicationState } from "store/rootReducer";
import NotificationConfigDrawer from "views/Notification/NotificationConfigDrawer";
import NotificationMessage from "views/Notification/NotificationMessage";
import HeaderLinkButton from "./HeaderLinkButton";

/////////////////////////////////////////////////////////////////////////////////////////////////

const FilterMenu: React.FC<
    {
        selectedFilter: NotificationMainGroupType,
        notifications: NotificationDto[];
        handleClickMenuFilter: (value: NotificationMainGroupType) => void;
    }> = ({ selectedFilter, notifications, handleClickMenuFilter }) => {

        const [filterTarget, setFilterTarget] = useState(null);
        const { t } = useTranslation(["clientApp", "sys"]);

        var groups: Array<{
            key: NotificationMainGroupType,
            text: string;
        }> = [];

        notifications.forEach((v) => {
            if (groups.findIndex(x => x.key == v.mainGroupType) == -1) {
                groups.push({ key: v.mainGroupType, text: t(`sys:NotificationMainGroupType.${NotificationMainGroupType[v.mainGroupType]}`) });
            }
        });

        const tabNames = groups.sort((a, b) => a.text.localeCompare(b.text));

        return (
            <>
                <IconButtonFilter
                    color={selectedFilter === null ? "inherit" : "warning"}
                    size="small"
                    onClick={
                        (e) => {
                            setFilterTarget(e.currentTarget);
                        }
                    }
                />

                <Menu
                    open={filterTarget != null}
                    anchorEl={filterTarget}
                    onClose={() => { setFilterTarget(null); handleClickMenuFilter(null); }}
                >
                    <MuiMenuItemChecked
                        value={null}
                        currentSelection={selectedFilter}
                        onClick={(v) => { setFilterTarget(null); handleClickMenuFilter(v); }}
                        text={t("Common.Filter.NoFilter")}
                    />
                    {
                        tabNames.map((v) =>
                            <MuiMenuItemChecked
                                key={NotificationMainGroupType[v.key]}
                                value={v.key}
                                text={v.text}
                                currentSelection={selectedFilter}
                                onClick={(k) => { setFilterTarget(null); handleClickMenuFilter(k); }}
                            />
                        )
                    }
                </Menu>
            </>
        );
    };

/////////////////////////////////////////////////////////////////////////////////////////////////


interface IStateProps {
    notifications: number,
}

type IDispatchProps = typeof AppActions;

interface IState {
    open: boolean;
    dlg: any;
    notificationTarget: any;
    selectedNotification: NotificationDto;
    notifications: NotificationDto[];
    selectedFilter: NotificationMainGroupType;
    postingAction: boolean;
}

interface IProps {
}

type Props = IStateProps & IDispatchProps & IProps & RouteComponentProps & WithTranslation & StyledComponentProps<HeaderStylesClassKey>;

/////////////////////////////////////////////////////////////////////////////////////////////////

class HeaderNotification extends React.Component<Props, IState> {
    private dropdownWrapperRef: HTMLElement;
    private counterTimer: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            open: false,
            dlg: null,
            notifications: null,
            notificationTarget: null,
            selectedNotification: null,
            selectedFilter: null,
            postingAction: false
        };
    }

    ///
    private handleToggle = ()=> {
        this.setState({ open: !this.state.open });
    };

    ///
    private handleCloseMenu = () => {
        this.setState({ open: false, notifications: null, selectedNotification: null, notificationTarget: null });
    };

    ///
    private handleClose = (e: MouseEvent) => {
        // Mantém o menu dropdown aberto se foi clicado uam opção do menu vertical.
        if (!HtmlUtils.checkMouseInsideHtmlElement(this.dropdownWrapperRef, e) && this.state.dlg == null)
            this.handleCloseMenu();
    };

    ///
    private startCounterMonitoring() {
        coreApi.getNotificationCounter().then((counter) => {
            this.props.updateNotificationCounter(counter);
            this.counterTimer = setTimeout(() => { this.startCounterMonitoring(); }, 60000);
        });
    }

    ///
    private endCounterMonitoring() {
        if (this.counterTimer)
            clearTimeout(this.counterTimer);
    }

    ///
    private getMessages() {
        coreApi.getNotifications().then((notifications) => {
            this.setState({ notifications });
            this.props.updateNotificationCounter(0);
        });
    }

    ///
    private cleanMessages = () => {
        coreApi.hideAllNotifications().then(() => {
            this.setState({ notifications: null });
        });
    };

    ///
    private handleClickMenuFilter(value: NotificationMainGroupType) {
        this.setState({ selectedFilter: value });
    }

    ///
    private handleClickMenuItem(action: string) {

        switch (action) {
            case "hideNotification":
                this.setState({ postingAction: true, notificationTarget: null }, () => {
                    coreApi.hideNotification(this.state.selectedNotification.notificationId)
                        .then(
                            (result) => {
                                if (result)
                                    this.setState({
                                        selectedNotification: null,
                                        notifications: this.state.notifications.filter(p => p.notificationId != this.state.selectedNotification.notificationId)
                                    });
                                else
                                    this.setState({ selectedNotification: null });
                            }
                        )
                        .finally(() => this.setState({ postingAction: false }));
                });
                break;

            case "blockNotification":
                {
                    const dto: NotificationSetupItemDto = {
                        subGroupType: this.state.selectedNotification.subGroupType,
                        notificationType: this.state.selectedNotification.notificationType,
                        options: {
                            blockNotification: true,
                            emailOption: null
                        }
                    };

                    this.setState({ postingAction: true, notificationTarget: null }, () => {
                        coreApi.updateUserNotificationConfiguration(dto)
                            .then(
                                () => {
                                    this.setState({
                                        selectedNotification: null,
                                        notifications: this.state.notifications.filter(p => p.notificationType != this.state.selectedNotification.notificationType)
                                    });
                                }
                            )
                            .finally(() => this.setState({ postingAction: false }));
                    });
                }
                break;

            case "stopFollowing":
                this.setState({ postingAction: true, notificationTarget: null }, () => {
                    kbApi.unfollowPublication(this.state.selectedNotification.targetPublicationId)
                        .then(
                            (result) => {
                                if (result)
                                    this.setState({
                                        selectedNotification: null,
                                        notifications: this.state.notifications.filter(p => p.notificationId != this.state.selectedNotification.notificationId)
                                    });
                                else
                                    this.setState({ selectedNotification: null });
                            }
                        )
                        .finally(() => this.setState({ postingAction: false }));
                });
                break;

            default:
                this.setState({ selectedNotification: null, notificationTarget: null });
        }
    }

    ///
    private getMenuOptions(): JSX.Element[] {
        const { postingAction } = this.state;

        const result = [];
        result.push(<MenuItem key="hideNotification" disabled={postingAction} onClick={() => this.handleClickMenuItem("hideNotification")}><AppResource resource="Common.Notification.HideNotification" /></MenuItem>);

        if (this.state.selectedNotification) {
            result.push(<MenuItem key="blockNotification" disabled={postingAction} onClick={() => this.handleClickMenuItem("blockNotification")}>
                {
                    this.state.selectedNotification.subGroupType == null
                        ? <AppResource resource="Common.Notification.BlockNotification" />
                        : "Não mostrar este notificações deste grupo"
                }
            </MenuItem>);

            if (this.state.selectedNotification.followingPublication)
                result.push(<MenuItem key="stopFollow" disabled={postingAction} onClick={() => this.handleClickMenuItem("stopFollow")}><AppResource resource="Common.Follow.Stop" /></MenuItem>);
        }

        return result;
    }

    ///
    private renderMessageMenu() {
        return (
            <Menu
                open={this.state.selectedNotification != null}
                anchorEl={this.state.notificationTarget}
                onClose={() => this.handleClickMenuItem(null)}>
                {this.getMenuOptions()}
            </Menu>
        );
    }


    ///
    private renderMessages() {
        const { postingAction } = this.state;

        if (!this.state.open)
            return null;

        if (this.state.notifications == null) {
            this.getMessages();
            return <ListItem><SpinnerIndicator /></ListItem>;
        }

        if (this.state.notifications.length == 0)
            return <ListItem><ListItemText primary={<AppResource resource="Common.Notification.EmptyNotificationList" />} /></ListItem>;

        const data = this.state.selectedFilter === null
            ? this.state.notifications
            : this.state.notifications.filter(p => p.mainGroupType == this.state.selectedFilter);

        return (
            <RequestWrapIndicator enabled={postingAction} type="post">
                {
                    data.map((n) => {
                        return (
                            <NotificationMessage
                                key={n.notificationId}
                                notification={n}
                                containerClassName={this.props.classes.dropdownListContainer}
                                newItemClassName={this.props.classes.newItemLeftMark}
                                avatarClassName={this.props.classes.dropdownListItemAvatar}
                                onCloseMenu={this.handleCloseMenu}
                                onClick={this.handleClose}
                                onItemMenuClick={(e, notification) => { this.setState({ selectedNotification: notification, notificationTarget: e.currentTarget }); }}
                            />
                        );
                    })
                }
            </RequestWrapIndicator>

        );
    }

    ///
    private renderDropdown() {
        const { classes } = this.props;

        return (
            <div className={classes.dropdownWrapper} ref={(node) => this.dropdownWrapperRef = node}>

                <Card
                    classes={{ root: classes.dropdownCard, }}
                >
                    <CardHeader
                        action={
                            <IconButtonCog
                                color="black"
                                onClick={
                                    () => {
                                        this.setState({
                                            dlg: <NotificationConfigDrawer
                                                classes={this.props.classes}
                                                onClose={
                                                    () => this.setState({ dlg: null })
                                                }
                                            />
                                        });
                                    }
                                }
                            />
                        }
                        title={
                            <>
                                <AppResource resource="Common.Notification.Title" />

                                {
                                    this.state.notifications != null &&
                                    <FilterMenu
                                        handleClickMenuFilter={(v) => this.handleClickMenuFilter(v)}
                                        notifications={this.state.notifications}
                                        selectedFilter={this.state.selectedFilter}
                                    />
                                }
                            </>
                        }
                        classes={{ root: classes.dropdownCardHeader, action: classes.dropdownCardHeaderAction }}
                    >
                    </CardHeader>

                    <CardContent
                        classes={{ root: classes.dropdownCardContent }}
                    >
                        <List role="notifications" className={classes.dropdownListRoot} component="nav" disablePadding>

                            <div className={classes.dropdownListContainerWrapper}>
                                {this.renderMessages()}
                            </div>

                        </List>

                    </CardContent>

                    {
                        this.state.notifications && this.state.notifications.length > 0 &&
                        <CardActions>
                            <Button onClick={this.cleanMessages} fullWidth><AppResource resource="Common.Notification.MarkRead" /></Button>
                        </CardActions>
                    }

                </Card>

                {this.renderMessageMenu()}

                {this.state.dlg}
            </div>
        );
    }

    ///
    public componentDidMount() {
        this.startCounterMonitoring();
    }

    ///
    public componentWillUnmount() {
        this.endCounterMonitoring();
    }

    ///
    public render() {
        const { classes } = this.props;

        return (
            <HeaderLinkButton
                accessKey={AccessKeys.Notification}
                classes={classes}
                icon={<IconNotification />}
                open={this.state.open}
                handleClose={this.handleClose}
                handleToggle={this.handleToggle}
                counter={this.props.notifications}
                tooltip={this.props.t("Common.TopHeader.NotificationTitle")}
            >
                {this.renderDropdown()}
            </HeaderLinkButton>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        notifications: state.app.notifications
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<IDispatchProps>>): IDispatchProps {
    return bindActionCreators(AppActions, dispatch);
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(HeaderNotification)));
